// flowgenIgnore
import type { ComponentType } from 'react';
import React from 'react';
import { Button as RebassButton } from 'rebass/styled-components';

import { Loading } from '@rover/icons';
import { Box, Flex, Text } from '@rover/kibble/components';
import type { ThemeSpacing, ThemeTextSizes } from '@rover/kibble/styles';
import { DSTokenMap } from '@rover/kibble/styles';

import type { MarginSystemProps, SizeSystemType, StringSystemType } from '../styledSystemPropTypes';

import type { ButtonSizeMap, ButtonSizes, ButtonVariants } from './utils';
import { getFullWidthPropForContainer, getVariant } from './utils';
// Size of the button is based off of the line height plus top and bottom padding
const ButtonHeightMap: ButtonSizeMap<ThemeSpacing> = {
  small: '2x',
  default: '3x',
  large: '5x',
};
// Size of the button is based off of the line height plus top and bottom padding
const ButtonWidthMap: ButtonSizeMap<ThemeSpacing> = {
  small: '4x',
  default: '6x',
  large: '8x',
};
const ButtonSizeTextSizeMap: ButtonSizeMap<ThemeTextSizes> = {
  small: '100',
  default: '200',
  large: '300',
};
// TO-DO Remove when sizes are added to Icon components
const ButtonIconSizeMap: ButtonSizeMap<string> = {
  small: DSTokenMap.SPACE_4X,
  default: DSTokenMap.SPACE_6X,
  large: DSTokenMap.SPACE_6X,
};
const ButtonIconMarginMap: ButtonSizeMap<ThemeSpacing> = {
  small: '2x',
  default: '3x',
  large: '4x',
};
export type Props = MarginSystemProps & {
  variant?: ButtonVariants;
  size?: ButtonSizes;
  disabled?: boolean;
  destructive?: boolean;
  contrast?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ComponentType<any>;
  iconAlignment?: 'left' | 'right';
  // styled-system
  width?: SizeSystemType;
  alignSelf?: StringSystemType;
  // react + html props
  children?: any;
  onClick?: (arg0: any) => any;
  type?: 'button' | 'submit' | 'reset';
  href?: string;
  role?: string;
  tabIndex?: number;
  'aria-label'?: string;
  'data-qa-id'?: string;
  // RxN
  testID?: string;
  accessibilityRole?: string;
  accessibilityLabel?: string;
  accessibilityHint?: string;
};

function Button({
  size = 'default',
  children,
  variant = 'default',
  disabled = false,
  destructive = false,
  contrast = false,
  loading = false,
  type = 'button',
  icon,
  iconAlignment = 'left',
  fullWidth = false,
  href,
  onClick,
  ...other
}: Props): any {
  const isLink = Boolean(href) && !onClick;
  return (
    <RebassButton
      variant={getVariant(variant, disabled, destructive, contrast)}
      px={ButtonWidthMap[size]}
      py={ButtonHeightMap[size]}
      type={isLink ? null : type}
      as={isLink ? 'a' : 'button'}
      href={disabled || !isLink ? null : href}
      onClick={onClick}
      {...getFullWidthPropForContainer(fullWidth)}
      {...other}
    >
      <Flex
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        gap={ButtonIconMarginMap[size]}
      >
        {icon && iconAlignment === 'left' ? (
          /* @ts-ignore: fill needs to pass through to icon */
          <Box
            as={icon}
            height={ButtonIconSizeMap[size]}
            width={ButtonIconSizeMap[size]}
            fill="currentColor"
            flexShrink="0"
          />
        ) : null}
        {children && (
          <Text size={ButtonSizeTextSizeMap[size]} fontWeight="semibold">
            {children}
          </Text>
        )}
        {icon && iconAlignment === 'right' ? (
          /* @ts-ignore: fill needs to pass through to icon */
          <Box
            as={icon}
            height={ButtonIconSizeMap[size]}
            width={ButtonIconSizeMap[size]}
            fill="currentColor"
            flexShrink="0"
          />
        ) : null}
        {loading && (
          <Box flexShrink="0" height={ButtonIconSizeMap[size]} width={ButtonIconSizeMap[size]}>
            <Loading
              fill="currentColor"
              width={ButtonIconSizeMap[size]}
              height={ButtonIconSizeMap[size]}
            />
          </Box>
        )}
      </Flex>
    </RebassButton>
  );
}

export default Button;



// WEBPACK FOOTER //
// ../kibble/components/Button/Button.tsx